import { createContext, useContext, useMemo } from 'react';

import FeatureGates, {
  CustomAttributes,
  FeatureGateEnvironment,
  Identifiers,
} from '@atlaskit/feature-gate-js-client';
import { uncurlyUuid } from '@atlassian/bitkit-analytics';
import {
  FeatureGatesInitialization,
  FeatureGatesInitializationProps,
} from '@atlassian/feature-gates-react';

import { CurrentDataContext } from 'src/app/current-data';
import { AppDataContext } from 'src/app/data';
import { StatsigKeys } from 'src/config/feature-flag-keys';
import { BbEnv, getBbEnv } from 'src/utils/bb-env';

export type StatsigGateContextType = {
  featureGates: typeof FeatureGates;
};

export type StatsigPropsThatChange = Pick<
  FeatureGatesInitializationProps,
  'identifiers' | 'customAttributes'
>;

export const StatsigGateContext = createContext<StatsigGateContextType>({
  featureGates: FeatureGates,
});

export const statsigEnvMap: { [bbEnv in BbEnv]: FeatureGateEnvironment } = {
  [BbEnv.Development]: FeatureGateEnvironment.Development,
  [BbEnv.Test]: FeatureGateEnvironment.Production, // Test is prod-like for application changes
  [BbEnv.Staging]: FeatureGateEnvironment.Staging,
  [BbEnv.Integration]: FeatureGateEnvironment.Staging,
  [BbEnv.Production]: FeatureGateEnvironment.Production,
};

const statsigOptions = (function () {
  const env = statsigEnvMap[getBbEnv() || BbEnv.Development];
  const apiKey = StatsigKeys[env];

  return { apiKey, environment: env, targetApp: 'bitbucket-cloud_web' };
})();

export const useFeatureIdsAndAttributes = () => {
  const appData = useContext(AppDataContext);
  const currentData = useContext(CurrentDataContext);
  const featureIdsAndAttributes: StatsigPropsThatChange = useMemo(() => {
    const customAttributes: CustomAttributes = {
      atlassian_staff: appData.user?.isAtlassian ?? false,
      bitbucket_team: appData.user?.isBitbucket ?? false,
    };
    const identifiers: Identifiers = {
      atlassianAccountId: appData.user?.aaid ?? undefined,
      bitbucketRepositoryId: currentData.repository?.uuid
        ? uncurlyUuid(currentData.repository.uuid)
        : undefined,
      bitbucketWorkspaceId: currentData.workspace?.uuid
        ? uncurlyUuid(currentData.workspace.uuid)
        : undefined,
    };

    return {
      customAttributes,
      identifiers,
    };
  }, [appData.user, currentData.workspace?.uuid, currentData.repository?.uuid]);

  return featureIdsAndAttributes;
};

export const StatsigFeatureProvider = ({
  children,
}: React.PropsWithChildren) => {
  const featureIdsAndAttributes = useFeatureIdsAndAttributes();

  return (
    <FeatureGatesInitialization
      options={statsigOptions}
      {...featureIdsAndAttributes}
    >
      {children}
    </FeatureGatesInitialization>
  );
};
